import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import config from '@iotv/config';
import { roundDecimals, TimeBucketUnits } from '@iotv/datamodel-core';
import { Milliseconds, SortOrder } from '@iotv/iotv-v3-types';
import { Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { NetWorkDAO } from '../../../data/NetworkDAO';
import { useModularTimePeriodSelector } from '../../../hooks/useModularTimePeriodSelector';
import { ObjectViewType } from '../../../types/AppTypes';
import { useColumnVisibilityForDataGrid } from '../../../v2/components/generic/ObjectsDataTable/hooks/useColumnVisibility';
import { StripedDataGrid } from '../../factories/DataGrid/styles/striped';
import { NetworkColumnMap, NetworkListViewDefintion } from './commonNetworkViewDefinition';
import { LoadingAnimationModal } from '../../../Popup/Loading';
const dynamo = config.aws.dynamoDB;
const maxCols = 10;
const TimePeriod = TimeBucketUnits.DAY;
const dev = process.env.REACT_APP_DEBUG;
const externalColumnVisibility = Object.fromEntries(Object.keys(NetworkListViewDefintion)
    .slice(maxCols)
    .map((k) => [k, false]));
const getAllServers = async () => {
    const gateways = await ConsumerDao.baseQuery({
        TableName: dynamo.tableName,
        IndexName: dynamo.typesiName,
        ExpressionAttributeNames: {
            '#pk': dynamo.pKey,
            '#type': dynamo.typesiPrimaryKey,
        },
        ExpressionAttributeValues: {
            ':type': 'LoRaWANApplicationServer',
        },
        KeyConditionExpression: '#type = :type',
        FilterExpression: 'begins_with(#pk, :type)',
        ScanIndexForward: false,
    });
    return gateways.data;
};
const getBucketedData = async (startDay, endDay) => {
    const query = {
        units: TimePeriod,
        duration: 1,
        fromDateValue: startDay,
        toDateValue: endDay,
        sortOrder: SortOrder.DESCENDING,
    };
    const { data } = await NetWorkDAO.geLNSMetricsFromTimestream(query);
    if (data) {
        return data;
    }
};
const calculateMonthPeriod = () => {
    const startDay = dayjs().date(1).subtract(1, 'month').hour(0).minute(0).second(0);
    const endDay = dayjs().date(1).hour(0).minute(0).second(0);
    return [startDay, endDay];
};
const NetworkListCard = () => {
    const [startDay, endDay] = calculateMonthPeriod();
    const [loading, setLoading] = useState(true);
    const days = Math.round((endDay.valueOf() - startDay.valueOf()) / Milliseconds.DAY);
    const { components, fromDateValue, toDateValue } = useModularTimePeriodSelector({
        rangeUnit: 'days',
        rangeLength: days,
        allowedRangeUnits: ['days'],
        behaviourFlags: { preventPeriodShiftIntoFuture: true },
        initialToTime: endDay,
        componentVariant: 'DatePicker',
        requireConfirmation: true,
    });
    const { fromPicker, toPicker, confirmButton } = components;
    const [LoRaWANApplicationServer, setLoRaWANApplicationServer] = useState();
    const [formattedNetworks, setFormattedNetworks] = useState(undefined);
    const [columnVisibilityModel, setColumnVisibilityModel, onColumnVisibilityModelChange] = useColumnVisibilityForDataGrid({
        dataTableReference: 'networkDataTable',
        objectTypeId: 'networkData',
        viewType: ObjectViewType.List,
        externalColumnVisibility,
    });
    useEffect(() => {
        getAllServers().then((foundServers) => {
            if (foundServers !== null && foundServers.Items !== undefined) {
                setLoRaWANApplicationServer(foundServers.Items);
            }
            else {
                setLoRaWANApplicationServer([]);
            }
        });
    }, []);
    useEffect(() => {
        const formatData = async (networkOptions) => {
            const relevantMetrics = await getBucketedData(fromDateValue, toDateValue);
            if (relevantMetrics === undefined) {
                setLoading(false);
                return;
            }
            const networkToBuckets = {};
            networkOptions.forEach((network) => {
                networkToBuckets[network.id] = [];
            });
            relevantMetrics.forEach((bucket) => {
                const id = bucket.id;
                networkToBuckets[id] = [...networkToBuckets[id], bucket];
            });
            const formattedRes = Object.keys(networkToBuckets).map((networkID) => {
                const buckets = networkToBuckets[networkID];
                const combiningGateways = buckets.reduce((acc, cur) => {
                    acc.gatewayCount = Math.max(acc.gatewayCount, cur.gateways);
                    acc.missedMessages += cur.droppedMsgs;
                    acc.uplinkCount += cur.uplinks;
                    acc.downlinkCount += cur.downlinks;
                    acc.messagesPerPeriodMax = Math.max(acc.messagesPerPeriodMax, cur.messagesPerPeriodMax);
                    acc.currentRate = Math.max(acc.currentRate, cur.currentRate);
                    return acc;
                }, {
                    missedMessages: 0,
                    uplinkCount: 0,
                    downlinkCount: 0,
                    messagesPerPeriodMax: 0,
                    currentRate: 0,
                    gatewayCount: 0,
                });
                const network = networkOptions.find((networkOption) => networkOption.id === networkID);
                let name = network?.name ?? networkID;
                return {
                    id: networkID,
                    name: name,
                    ...combiningGateways,
                    gatewayCount: roundDecimals(combiningGateways.gatewayCount, 1),
                    currentRate: roundDecimals(combiningGateways.currentRate, 1),
                };
            });
            setFormattedNetworks(formattedRes);
            setLoading(false);
        };
        if (LoRaWANApplicationServer) {
            setLoading(true);
            formatData(LoRaWANApplicationServer);
        }
    }, [LoRaWANApplicationServer, fromDateValue, toDateValue]);
    const dataGridProps = {
        density: 'compact',
        columns: Object.entries(NetworkColumnMap).map(([k, v]) => ({ field: k, ...v })),
        rows: formattedNetworks ?? [],
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        loading: false,
        columnVisibilityModel,
        onColumnVisibilityModelChange,
        rowHeight: 32,
        columnHeaderHeight: 40,
    };
    return (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h5", gutterBottom: true, children: "Network List" }), _jsxs(Stack, { direction: "row", spacing: 2, sx: {
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                }, children: [fromPicker, " ", toPicker, " ", confirmButton] }), _jsx("br", {}), _jsx(StripedDataGrid, { style: { fontSize: 'inherit', height: '100cqh' }, ...dataGridProps }), loading && _jsx(LoadingAnimationModal, {})] }));
};
export default NetworkListCard;
